<script setup>
// A OPERADORA E OS NÚMEROS (telefonia, etapa D, 25/09/2026) — Integrações ›
// Telefonia, para quem instala. Cada número do escritório na operadora (DID)
// escolhe o ramal que atende; mudar vai para a central na hora. A primeira
// operadora ligada é por onde os ramais ligam para fora.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TelefoniaAPI from 'dashboard/api/telefonia';
import AdvupSelect from 'dashboard/components-next/AdvupSelect.vue';
import EstadoVazio from 'dashboard/components-next/EstadoVazio.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import OperadoraModal from './OperadoraModal.vue';
import { opcoesDeDestino } from './roteamento/destinos';

const props = defineProps({
  ramais: { type: Array, default: () => [] },
  roteamento: {
    type: Object,
    default: () => ({ filas: [], uras: [], horarios: [] }),
  },
});
const emit = defineEmits(['ramaisMudaram']);
const operadoras = defineModel('operadoras', {
  type: Array,
  default: () => [],
});

const { t } = useI18n();
const T = 'TELEFONIA.OPERADORA';

const trocar = atualizada => {
  const i = operadoras.value.findIndex(o => o.id === atualizada.id);
  if (i >= 0) operadoras.value.splice(i, 1, atualizada);
  else operadoras.value.push(atualizada);
};

// (27) 3333-4444 / (27) 99999-0000
const formatar = numero => {
  const d = String(numero || '');
  if (d.length === 11)
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10)
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return d;
};
const estadoDe = o => {
  if (!o.ativo) return 'desligada';
  if (o.erro) return 'erro';
  return o.no_pbx ? 'ok' : 'pendente';
};
const TOM = {
  ok: 'ok',
  erro: 'alerta',
  desligada: 'neutro',
  pendente: 'ambar',
};

// número atende em ramal, fila, URA ou horário (etapa E)
const contexto = computed(() => ({
  ramais: props.ramais,
  filas: props.roteamento.filas || [],
  uras: props.roteamento.uras || [],
  horarios: props.roteamento.horarios || [],
}));
const opcoesDeRamal = computed(() => opcoesDeDestino(contexto.value, t));

// ── Modal ───────────────────────────────────────────────────────────────────
const modal = ref(null); // null | 'nova' | operadora
const salvando = ref(false);
const erroDoModal = ref('');

async function salvar(dados) {
  salvando.value = true;
  erroDoModal.value = '';
  try {
    const { data } =
      modal.value === 'nova'
        ? await TelefoniaAPI.criarOperadora(dados)
        : await TelefoniaAPI.atualizarOperadora(modal.value.id, dados);
    trocar(data.operadora);
    modal.value = null;
    emit('ramaisMudaram');
    useAlert(data.operadora.erro ? t(`${T}.SALVA_COM_ERRO`) : t(`${T}.SALVA`));
  } catch (e) {
    erroDoModal.value = e?.response?.data?.error || t(`${T}.FALHOU`);
  } finally {
    salvando.value = false;
  }
}

async function reenviar(o) {
  const { data } = await TelefoniaAPI.reenviarOperadora(o.id);
  trocar(data.operadora);
  useAlert(
    data.operadora.erro ? t(`${T}.SALVA_COM_ERRO`) : t(`${T}.REENVIADA`)
  );
}

// ── Apagar (com confirmação) ────────────────────────────────────────────────
const dialogo = ref(null);
const paraApagar = ref(null);
const ocupado = ref(false);
function pedirApagar(o) {
  paraApagar.value = o;
  dialogo.value?.open();
}
async function apagar() {
  ocupado.value = true;
  try {
    await TelefoniaAPI.apagarOperadora(paraApagar.value.id);
    operadoras.value = operadoras.value.filter(
      o => o.id !== paraApagar.value.id
    );
    emit('ramaisMudaram');
    dialogo.value?.close();
  } finally {
    ocupado.value = false;
  }
}

// ── Números ─────────────────────────────────────────────────────────────────
const novos = ref({}); // por operadora: { numero, destino }
const novo = o => {
  if (!novos.value[o.id]) {
    novos.value[o.id] = {
      numero: '',
      destino: opcoesDeRamal.value[0]?.value || '',
    };
  }
  return novos.value[o.id];
};
const erroDoNumero = ref({});

async function acrescentar(o) {
  const n = novo(o);
  erroDoNumero.value[o.id] = '';
  try {
    const { data } = await TelefoniaAPI.criarNumero({
      operadora_id: o.id,
      number: n.numero,
      destination: n.destino,
    });
    trocar(data.operadora);
    n.numero = '';
  } catch (e) {
    erroDoNumero.value[o.id] = e?.response?.data?.error || t(`${T}.FALHOU`);
  }
}
async function mudarDestino(item, destino) {
  const { data } = await TelefoniaAPI.atualizarNumero(item.id, {
    destination: destino,
  });
  trocar(data.operadora);
}
async function removerNumero(item) {
  const { data } = await TelefoniaAPI.apagarNumero(item.id);
  trocar(data.operadora);
}
</script>

<template>
  <section class="tel-op">
    <header class="tel-op__cab">
      <h2 class="tel-op__tit">{{ t(`${T}.TIT`) }}</h2>
      <button
        v-if="operadoras.length"
        type="button"
        class="advup-btn--vazado"
        @click="modal = 'nova'"
      >
        <i class="i-ph-plus-bold" aria-hidden="true" />
        {{ t(`${T}.OUTRA`) }}
      </button>
    </header>

    <EstadoVazio
      v-if="!operadoras.length"
      class="advup-card"
      icone="i-ph-broadcast"
      :titulo="t(`${T}.VAZIO_TIT`)"
      :dica="t(`${T}.VAZIO_DICA`)"
    >
      <button type="button" class="advup-btn-forte" @click="modal = 'nova'">
        <i class="i-ph-broadcast-fill" aria-hidden="true" />
        {{ t(`${T}.CONECTAR`) }}
      </button>
    </EstadoVazio>

    <article
      v-for="(o, i) in operadoras"
      :key="o.id"
      class="advup-card tel-op__card"
    >
      <div class="tel-op__topo">
        <span class="tel-op__ico" aria-hidden="true">
          <i class="i-ph-broadcast-fill" />
        </span>
        <div class="tel-op__id">
          <p class="tel-op__nome">
            {{ o.nome }}
            <span v-if="i === 0 && o.ativo" class="tel-op__principal">
              {{ t(`${T}.PRINCIPAL`) }}
            </span>
          </p>
          <p class="tel-op__sub">
            {{ o.host }}:{{ o.porta }} ·
            {{ t(`${T}.MODO_${o.modo.toUpperCase()}`) }}
            <template v-if="o.bina">
              · {{ t(`${T}.BINA_CURTA`, { numero: o.bina }) }}
            </template>
          </p>
        </div>
        <span class="ficha-pill" :class="`ficha-pill--${TOM[estadoDe(o)]}`">
          {{ t(`${T}.ESTADO.${estadoDe(o).toUpperCase()}`) }}
        </span>
        <div class="tel-op__acoes">
          <button
            v-if="o.erro"
            type="button"
            class="advup-acao"
            :aria-label="t(`${T}.REENVIAR`)"
            @click="reenviar(o)"
          >
            <i class="i-hugeicons-reload" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="advup-acao"
            :aria-label="t(`${T}.EDITAR`)"
            @click="modal = o"
          >
            <i class="i-hugeicons-pencil-edit-02" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="advup-acao advup-acao--perigo"
            :aria-label="t(`${T}.APAGAR`)"
            @click="pedirApagar(o)"
          >
            <i class="i-hugeicons-delete-02" aria-hidden="true" />
          </button>
        </div>
      </div>

      <p v-if="o.erro" class="tel-op__erro" role="alert">
        <i class="i-ph-warning-octagon-fill" aria-hidden="true" />
        {{ o.erro }}
      </p>

      <div class="tel-op__numeros">
        <p class="advup-rotulo">{{ t(`${T}.NUMEROS`) }}</p>
        <ul v-if="o.numeros.length" class="tel-op__lista">
          <li v-for="n in o.numeros" :key="n.id" class="tel-op__linha">
            <span class="tel-op__num">{{ formatar(n.numero) }}</span>
            <i
              class="i-hugeicons-arrow-right-01 tel-op__seta"
              aria-hidden="true"
            />
            <AdvupSelect
              :model-value="n.destino"
              :options="opcoesDeRamal"
              :rotulo="t(`${T}.ATENDE`, { numero: formatar(n.numero) })"
              min-width="0"
              @update:model-value="v => mudarDestino(n, v)"
            />
            <button
              type="button"
              class="advup-acao advup-acao--perigo"
              :aria-label="t(`${T}.TIRAR`, { numero: formatar(n.numero) })"
              @click="removerNumero(n)"
            >
              <i class="i-hugeicons-cancel-01" aria-hidden="true" />
            </button>
          </li>
        </ul>
        <p v-else class="advup-nota">{{ t(`${T}.SEM_NUMEROS`) }}</p>

        <form class="tel-op__novo" @submit.prevent="acrescentar(o)">
          <input
            v-model="novo(o).numero"
            name="numero-novo"
            type="tel"
            inputmode="tel"
            autocomplete="off"
            class="advup-campo reset-base"
            :placeholder="t(`${T}.NUMERO_PLACEHOLDER`)"
            :aria-label="t(`${T}.NUMERO_PLACEHOLDER`)"
          />
          <AdvupSelect
            v-model="novo(o).destino"
            :options="opcoesDeRamal"
            :rotulo="t(`${T}.RAMAL_QUE_ATENDE`)"
            min-width="0"
          />
          <button
            type="submit"
            class="advup-btn-forte"
            :disabled="!novo(o).numero || !novo(o).destino"
          >
            {{ t(`${T}.ACRESCENTAR`) }}
          </button>
        </form>
        <p v-if="erroDoNumero[o.id]" class="advup-nota advup-nota--erro">
          {{ erroDoNumero[o.id] }}
        </p>
      </div>
    </article>

    <OperadoraModal
      v-if="modal"
      :operadora="modal === 'nova' ? null : modal"
      :ramais="ramais"
      :contexto="contexto"
      :salvando="salvando"
      :erro="erroDoModal"
      @close="modal = null"
      @salvar="salvar"
    />
    <Dialog
      ref="dialogo"
      type="alert"
      icone="i-ph-trash"
      :title="t(`${T}.APAGAR_TIT`, { nome: paraApagar?.nome || '' })"
      :description="t(`${T}.APAGAR_TXT`)"
      :confirm-button-label="t(`${T}.APAGAR`)"
      :is-loading="ocupado"
      @confirm="apagar"
    />
  </section>
</template>

<style scoped>
.tel-op {
  display: flex;
  flex-direction: column;
  gap: var(--esp-4);
}
.tel-op__cab {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--esp-3);
}
.tel-op__tit {
  margin: 0;
  font-size: var(--txt-bloco);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  color: rgb(var(--slate-12));
}
.tel-op__card {
  display: flex;
  flex-direction: column;
  gap: var(--esp-4);
  padding: var(--esp-5);
}
.tel-op__topo {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--esp-3);
}
.tel-op__ico {
  display: grid;
  flex-shrink: 0;
  place-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--r-3);
  font-size: var(--ico-3);
  background: rgb(var(--teal-3));
  color: rgb(var(--teal-11));
}
.tel-op__id {
  flex: 1;
  min-width: 0;
}
.tel-op__nome {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--esp-2);
  margin: 0;
  font-size: var(--txt-lista);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  color: rgb(var(--slate-12));
}
.tel-op__principal {
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  letter-spacing: var(--rastro-rotulo);
  text-transform: uppercase;
  color: rgb(var(--teal-11));
}
.tel-op__sub {
  margin: 2px 0 0;
  overflow: hidden;
  font-size: var(--txt-nota);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-11));
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tel-op__acoes {
  display: flex;
  gap: var(--esp-1);
}
.tel-op__erro {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-2);
  margin: 0;
  padding: var(--esp-3);
  border-radius: var(--r-3);
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  background: rgb(var(--ruby-3));
  color: rgb(var(--ruby-11));
}
.tel-op__numeros {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
}
.tel-op__lista {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.tel-op__linha {
  display: flex;
  align-items: center;
  gap: var(--esp-3);
  padding: var(--esp-2);
  border-radius: var(--r-3);
}
.tel-op__linha:hover {
  background: rgb(var(--slate-3));
}
.dark .tel-op__linha:hover {
  background: rgb(255 255 255 / 5%);
}
.tel-op__num {
  min-width: 140px;
  white-space: nowrap;
  font-size: var(--txt-dado);
  font-weight: var(--peso-dado);
  font-variant-numeric: tabular-nums;
  color: rgb(var(--slate-12));
}
.tel-op__seta {
  color: rgb(var(--slate-10));
}
.tel-op__linha > :nth-child(3) {
  flex: 0 1 260px;
}
.tel-op__novo > :nth-child(2) {
  flex: 0 1 220px;
}
.tel-op__novo {
  display: flex;
  flex-wrap: wrap;
  gap: var(--esp-2);
  padding-top: var(--esp-2);
}
.tel-op__novo .advup-campo {
  flex: 1;
  min-width: 180px;
}
@media (max-width: 639px) {
  /* o nome ganha a linha; selo e ações descem para a de baixo */
  .tel-op__id {
    flex-basis: calc(100% - 52px);
  }
  .tel-op__num {
    min-width: 0;
  }
  /* número e X na mesma linha; o ramal desce e ocupa a largura */
  .tel-op__linha {
    flex-wrap: wrap;
  }
  .tel-op__num {
    flex: 1;
  }
  .tel-op__seta {
    display: none;
  }
  .tel-op__linha > :nth-child(3) {
    flex: 1 1 100%;
    order: 2;
  }
  /* mesma especificidade da regra do computador (:nth-child), senão ela vence */
  .tel-op__novo > :nth-child(n) {
    flex: 1 1 100%;
  }
}
</style>
